import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "row gap-02" }

import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import { useAppUserStore } from "@/stores/appUser";
import EsWaitingRoomForm from "@/components/form/esWaitingRoomForm.vue";
import EsConfirmDialog from '@/components/modals/esConfirmDialog.vue';
import esWaitingRoomBadge from "@/components/esWaitingRoomBadge.vue";
import {
  InstituteApi,
  ApiError,
  ResponseError, WaitingRoomResponse
} from "@/apiGenerate/medserver";
import { medServerApiConfig } from "@/config/medserver-api.config";
import { WaitingRoomForm, WaitingRoom } from "@/models/institute.model";
import { Column, DataTable, useToast } from "primevue";
import { toastError, toastSuccess } from "@/utils/toast-utils";
import { useWaitingRoomsStore } from "@/stores/waitingRooms";


export default /*@__PURE__*/_defineComponent({
  __name: 'esWaitingRoomSettings',
  emits: ['refresh-calendar'],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();
const appUserStore = useAppUserStore();
const waitingRoomsStore = useWaitingRoomsStore();
const toast = useToast();

const instituteApi = new InstituteApi(medServerApiConfig);

const emits = __emit;

const dropd = ref("");
const isDeleteModalOpened = ref(false);
const isFormModalOpened = ref(false);
const waitingRooms = ref<WaitingRoom[]>([]);

const selectedAddressId = ref("");

const currentWaitingRoom = ref<WaitingRoom>({
  roomId: '',
  name: '',
  street: '',
  liveRoomDetails: '',
  byDefault: false,
  addressId: '',
  instituteId: '',
  roomBadgeId: null,
});

const currInstituteUUID = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  return appUserStore.currentHealthGroupMembership.instituteWithRole.instituteWithRoleId || "";
});


const openAddForm = () => {
  currentWaitingRoom.value = {
    roomId: '',
    name: '',
    street: '',
    liveRoomDetails: '',
    byDefault: false,
    addressId: '',
    instituteId: currInstituteUUID.value,
    roomBadgeId: null,
  };
  isFormModalOpened.value = true;
};

const openUpdateForm = (waitingRoom: WaitingRoom) => {
  dropd.value = "";
  currentWaitingRoom.value = { ...waitingRoom };
  isFormModalOpened.value = true;

  selectedAddressId.value = waitingRoom.addressId || "";
};

const onCloseFormModal = () => {
  isFormModalOpened.value = false;
}

const onConfirmFormModal = async (formData: WaitingRoomForm) => {
  if (formData.isEditing) {
    instituteApi.updateRoom({
          instituteId: currInstituteUUID.value,
          roomId: formData.roomId,
          updateInstituteRoom: {
            addressId: formData.addressId,
            liveRoomDetails: formData.name,
            byDefault: formData.byDefault ?? false,
            roomBadgeId: formData.roomBadgeId ?? null,
          }
        })
        .then(() => {
          toastSuccess(toast, {
              summary: t('common.toast.success.update'),
              detail:  t('settings.label.waitingRoom.message.success.update')
          });
          isFormModalOpened.value = false;
          waitingRoomsStore.updateWaitingRooms();
          appUserStore.setRefreshCalendar(true);
          return getAllWaitingRooms();
        })
        .catch(async (error: ResponseError) => {
          const apiError: ApiError = await error.response.json();
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: t(`error.${apiError.status}`)
          });
          throw error;
        });
  } else {
    instituteApi.addRoomToInstituteAddress(
          {
            instituteId: currInstituteUUID.value,
            createInstituteRoom: {
              addressId: formData.addressId,
              liveRoomDetails: formData.name,
              byDefault: formData.byDefault,
              roomBadgeId: formData.roomBadgeId ?? null,
            }
          }
      )
        .then(() => {
          toastSuccess(toast, {
              summary: t('common.toast.success.add'),
              detail:  t('settings.label.waitingRoom.message.success.add')
          });
          isFormModalOpened.value = false;
          emits('refresh-calendar', true);
          return getAllWaitingRooms();
        })
        .catch(async (error: ResponseError) => {
            const apiError: ApiError = await error.response.json();
            toastError(toast, {
                summary: t('common.toast.error'),
                detail: t(`error.${apiError.status}`)
            });
            throw error;
        });
  }
};

const waitingRoomHeaderKey = computed(() =>
    currentWaitingRoom.value?.roomId
        ? 'settings.label.waitingRoom.edit.header'
        : 'settings.label.waitingRoom.add.header'
)

const onCloseDeleteModal = () => {
  isDeleteModalOpened.value = false;
}

const onConfirmDeleteModal = async () => {
  instituteApi.deleteRoom({
      instituteId: currInstituteUUID.value,
      roomId: currentWaitingRoom.value.roomId,
    })
    .then(() => {
      toastSuccess(toast, {
          summary: t('common.toast.success.delete'),
          detail:  t('settings.label.waitingRoom.message.success.delete')
      });
      emits('refresh-calendar', true);
      getAllWaitingRooms();
      isDeleteModalOpened.value = false;
    })
    .catch(async (error: ResponseError) => {
      if (error.response) {
        const apiError: ApiError = await error.response.json();
        toastError(toast, {
            summary: t('common.toast.error'),
            detail: t(`error.${apiError.status}`)
        });
      }
      throw error;
    });
};

const openConfirmModal = (waitingRoom: WaitingRoom) => {
  dropd.value = "";
  currentWaitingRoom.value = { ...waitingRoom };
  isDeleteModalOpened.value = true;
};

const getAllWaitingRooms = async () => {
  instituteApi
      .listRoomsByInstitute({ instituteId: currInstituteUUID.value, withTemporary: false })
      .then((res: WaitingRoomResponse[]) => {
        waitingRooms.value = res.sort((a, b) => {
        if (!a.name) return -1;
        if (!b.name) return 1;
        return a.name.localeCompare(b.name);
      });
    });
};

watch(currInstituteUUID, async (newVal) => {
  if (newVal) {
    await getAllWaitingRooms();
  }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_svg_item = _resolveComponent("svg-item")!
  const _component_text_item = _resolveComponent("text-item")!
  const _component_buttonGroup = _resolveComponent("buttonGroup")!
  const _component_es_button_icon = _resolveComponent("es-button-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_buttonGroup, {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (openAddForm()))
      }, {
        default: _withCtx(() => [
          _createVNode(_component_svg_item, { icon: "plus" }),
          _createVNode(_component_text_item, {
            txt: _unref(t)('action.add'),
            styles: "font-mBold"
          }, null, 8, ["txt"])
        ]),
        _: 1
      })
    ]),
    (waitingRooms.value && waitingRooms.value.length > 0)
      ? (_openBlock(), _createBlock(_unref(DataTable), {
          key: 0,
          value: waitingRooms.value,
          "data-key": "roomId",
          "striped-rows": "",
          class: "mtop-06"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Column), {
              field: "name",
              header: _unref(t)('settings.label.waitingRoom.name'),
              style: {"min-width":"20rem"},
              class: "table-main-col"
            }, null, 8, ["header"]),
            _createVNode(_unref(Column), {
              field: "roomBadge",
              header: _unref(t)('settings.label.waitingRoom.badge')
            }, {
              body: _withCtx(({ data }) => [
                (data.roomBadge)
                  ? (_openBlock(), _createBlock(esWaitingRoomBadge, {
                      key: 0,
                      text: data.roomBadge.text,
                      color: data.roomBadge.color
                    }, null, 8, ["text", "color"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["header"]),
            _createVNode(_unref(Column), {
              field: "street",
              header: _unref(t)('settings.label.waitingRoom.street'),
              style: {"min-width":"20rem"}
            }, null, 8, ["header"]),
            _createVNode(_unref(Column), {
              field: "byDefault",
              header: _unref(t)('settings.label.waitingRoom.byDefault'),
              style: {"width":"10rem"}
            }, {
              body: _withCtx(({ data }) => [
                _createTextVNode(_toDisplayString(data.byDefault ? _unref(t)('common.yes') : _unref(t)('common.no')), 1)
              ]),
              _: 1
            }, 8, ["header"]),
            _createVNode(_unref(Column), {
              field: "actions",
              header: _unref(t)('settings.label.waitingRoom.action')
            }, {
              body: _withCtx(({ data }) => [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_es_button_icon, {
                    icon: "pencil_fill",
                    folder: "shareIcons",
                    class: "pad-l",
                    styles: "img-primary",
                    onClick: ($event: any) => (openUpdateForm(data))
                  }, null, 8, ["onClick"]),
                  _createVNode(_component_es_button_icon, {
                    icon: "trash",
                    folder: "shareIcons",
                    class: "pad-l",
                    styles: "img-primary",
                    onClick: ($event: any) => (openConfirmModal(data))
                  }, null, 8, ["onClick"])
                ])
              ]),
              _: 1
            }, 8, ["header"])
          ]),
          _: 1
        }, 8, ["value"]))
      : _createCommentVNode("", true),
    _createVNode(EsWaitingRoomForm, {
      currInstituteUUID: currInstituteUUID.value,
      isOpen: isFormModalOpened.value,
      waitingRoom: currentWaitingRoom.value,
      header: _unref(t)(waitingRoomHeaderKey.value),
      onModalClose: onCloseFormModal,
      "on-confirm": onConfirmFormModal
    }, null, 8, ["currInstituteUUID", "isOpen", "waitingRoom", "header"]),
    _createVNode(EsConfirmDialog, {
      isOpen: isDeleteModalOpened.value,
      onModalClose: onCloseDeleteModal,
      "on-confirm": onConfirmDeleteModal,
      header: _unref(t)('settings.label.waitingRoom.delete.header'),
      message: _unref(t)('settings.label.waitingRoom.delete.message')
    }, null, 8, ["isOpen", "header", "message"])
  ], 64))
}
}

})